<template>
  <span class="hidden" aria-hidden="true" />
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToaster } from "@shopexperts/shared";
import { useAuthStore } from "@/store/auth";
import { useNotifications } from "@/v2Projects/composables/useNotifications";
import { buildProjectUserError } from "@/v2Projects/userErrors";
import type { DashboardNotification } from "@/v2Projects/types";
import {
  getNotificationDeepLink,
  getNotificationDisplayBody,
} from "@/v2Projects/utils/notificationPresentation";

const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();
const toaster = useToaster();
const { notifications, isReady, isEnabled, viewerKey, listVersion, markRead, dismiss } = useNotifications({
  subscribeToUnreadCount: false,
});
const authState = computed(() => (authStore.token ? "authenticated" : "anonymous"));
const authUserId = computed(() => authStore.user?.id ?? null);

const seenRecipientIds = new Set<DashboardNotification["recipient_id"]>();
let hydratedSessionKey: string | null = null;
let sessionStartListVersion = listVersion.value;
let sessionStartedAt = Date.now();

function resetSeenNotifications() {
  seenRecipientIds.clear();
  hydratedSessionKey = null;
  sessionStartListVersion = listVersion.value;
  sessionStartedAt = Date.now();
}

function markNotificationsAsSeen(nextNotifications: DashboardNotification[]) {
  nextNotifications.forEach((notification) => {
    seenRecipientIds.add(notification.recipient_id);
  });
}

async function openNotification(notification: DashboardNotification) {
  try {
    if (!notification.is_read) {
      await markRead(notification.recipient_id);
    }
  } catch (error) {
    const message = buildProjectUserError("updateNotification", error);
    toaster.create({
      title: message.title,
      description: `${message.description} Opening the project anyway.`,
      type: "error",
    });
  }
  await router.push(getNotificationDeepLink(notification));
}

/**
 * A notification about the conversation or project on screen is redundant and is dropped.
 * A hidden tab is not on screen, so it keeps the toast and unread state like any other tab.
 */
function targetsCurrentView(notification: DashboardNotification): boolean {
  if (document.visibilityState !== "visible") return false;
  const projectId = Number(route.params.id);
  if (notification.thread_id && notification.chatroom_kind === "direct_chatroom") {
    return route.params.threadId === notification.thread_id;
  }
  if (notification.project_id && /\/projects\/\d+$/.test(route.path)) {
    return projectId === notification.project_id;
  }
  return false;
}

function showToast(notification: DashboardNotification) {
  toaster.create({
    title: notification.title,
    description: getNotificationDisplayBody(notification),
    type: "info",
    meta: {
      theme: "light",
      notification,
      onOpen: () => openNotification(notification),
    },
  });
}

watch(isEnabled, (enabled) => {
  if (!enabled) {
    resetSeenNotifications();
  }
}, { immediate: true });

watch(viewerKey, (nextViewerKey, previousViewerKey) => {
  if (nextViewerKey !== previousViewerKey) {
    resetSeenNotifications();
  }
}, { immediate: true });

watch(authState, (nextAuthState, previousAuthState) => {
  if (nextAuthState !== previousAuthState) {
    resetSeenNotifications();
  }
}, { immediate: true });

watch(authUserId, (nextUserId, previousUserId) => {
  if (nextUserId !== previousUserId) {
    resetSeenNotifications();
  }
}, { immediate: true });

watch([isReady, notifications, viewerKey, authState, authUserId, listVersion], ([
  ready,
  nextNotifications,
  nextViewerKey,
  nextAuthState,
  nextAuthUserId,
  nextListVersion,
]) => {
  if (!ready || !isEnabled.value) {
    return;
  }

  if (!nextViewerKey || nextAuthState !== "authenticated") {
    return;
  }

  const viewerUserId = Number(nextViewerKey.split(":")[1]);
  if (nextAuthUserId && viewerUserId !== nextAuthUserId) {
    return;
  }

  const nextSessionKey = `${nextViewerKey}:${nextAuthState}:${nextAuthUserId ?? "unknown"}`;
  if (hydratedSessionKey !== nextSessionKey) {
    if (nextListVersion <= sessionStartListVersion) {
      return;
    }
    markNotificationsAsSeen(nextNotifications);
    hydratedSessionKey = nextSessionKey;
    return;
  }

  const unseenNotifications = nextNotifications.filter(
    (notification) => !seenRecipientIds.has(notification.recipient_id) && notification.created_at >= sessionStartedAt,
  );

  if (unseenNotifications.length === 0) {
    return;
  }

  unseenNotifications
    .slice()
    .reverse()
    .forEach((notification) => {
      seenRecipientIds.add(notification.recipient_id);
      if (targetsCurrentView(notification)) {
        void dismiss(notification.recipient_id).catch(() => {});
        return;
      }
      showToast(notification);
    });
});
</script>
